import * as uamFlowTypes from '../../consts/uam/uamFlowTypes';
import {
    selectIsAutoCleanInProgress,
    selectIsConnectedToCm,
    selectIsConnectInProgress,
    selectIsFindMyDeviceInProgress,
    selectIsResetDeviceInProgress,
    selectIsUpdateSettingsInProgress,
} from '../../state/selectors/iotData';
import uamCheckStatusService from '../../consts/uam/uamCheckStatusService';
import {
    setAutoCleanInProgress,
    setConnectInProgress,
    setFindMyDeviceStatus,
    setFwuInProgress,
    setResetDeviceInProgress,
    setUpdateSettingsInProgress,
} from '../../state/slices/iotData';
import {checkConnectivityStatus} from '../iot/iotConnectionService';
import uamTimeouts from '../../consts/uam/uamTimeouts';
import iotWebSocketService from '../websocket/iotWebSocketService';
import errorService from '../errors/errorService';
import {MW_ERRORS} from '../../consts/app/mwErrors';
import findMyDeviceStatuses from '../../consts/device/findMyDeviceStatuses';

let flows = [];
let flowsTimeout = [];
let flowsRequestCheck = [];

const createFlow = (params) => {
    const {type, isHolder, requestId} = params;

    if (!type) throw new Error('Type must be provided');

    addFlow(requestId, type, isHolder);

    createFlowTimeout(params);
    createFlowRequestCheck(params);
};

const createFlowRequestCheck = (params) => {
    const {type, isHolder} = params;

    const instanceCheck = setTimeout(async () => {
        //if websocket is connected then all messages are retrieved from it
        if (iotWebSocketService.isWebSocketConnected(isHolder)) return;

        const isFlowValid = await runCheck(params);

        if (isFlowValid) createFlowRequestCheck(params);
    }, uamTimeouts.UAM_CHECK_STATUS_INTERVAL * 1000);

    addFlowRequestCheck(instanceCheck, type, isHolder);
};

const createFlowTimeout = (params) => {
    const {type, timeoutInSec, isHolder} = params;

    const instanceTimeout = setTimeout(async () => {
        const isFlowValid = await runCheck(params);

        if (isFlowValid) createFlowTimeout(params);
    }, timeoutInSec * 1000);

    addFlowTimeout(instanceTimeout, type, isHolder);
};

const runCheck = async (params) => {
    const {type, requestId, isHolder, data = null} = params;
    const isValid = isFlowValid(type, isHolder, data);

    if (!isValid) return;

    if (type === uamFlowTypes.CONNECT) {
        await checkConnectivityStatus(isHolder);
    } else {
        const isFlowFinished = await uamCheckStatusService.checkRequestStatus({
            isHolder,
            requestId,
            type,
            isTimeout: true,
        });

        if (isFlowFinished) return;
    }

    const isRequestValid = isRequestIdValid({type, isHolder, data});

    if (!isRequestValid) return;

    return true;
};

const isRequestIdValid = ({type, isHolder, data}) => {
    const requestId = getFlow(type, isHolder)?.requestId;

    if (requestId) return true;

    const error = errorService.createError(MW_ERRORS.ERROR_IOT_NO_REQUEST_ID, 'No request id in state to check');

    switch (type) {
        case uamFlowTypes.UPDATE_SETTINGS:
            setUpdateSettingsInProgress(isHolder, false, error);
            return false;
        case uamFlowTypes.FIND_MY_DEVICE:
            setFindMyDeviceStatus(findMyDeviceStatuses.ERROR, error);
            return false;
        case uamFlowTypes.RESET_DEVICE:
            setResetDeviceInProgress(isHolder, false, error);
            return false;
        case uamFlowTypes.AUTO_CLEAN:
            setAutoCleanInProgress(false, error);
            return false;
        case uamFlowTypes.UPDATE_FIRMWARE:
            setFwuInProgress(isHolder, false, error);
            return false;
        case uamFlowTypes.SYNCHRONIZE:
            setConnectInProgress(isHolder, false, error);
            return false;
        case uamFlowTypes.DATA_COLLECTION:
            return false;
        default:
            break;
    }

    return true;
};

const isFlowValid = (type, isHolder, data) => {
    switch (type) {
        case uamFlowTypes.UPDATE_SETTINGS:
            const isUpdateSettingsInProgress = selectIsUpdateSettingsInProgress(isHolder);

            if (!isUpdateSettingsInProgress) return false;

            break;
        case uamFlowTypes.AUTO_CLEAN:
            const isAutoCleanInProgress = selectIsAutoCleanInProgress();

            if (!isAutoCleanInProgress) return false;

            break;
        case uamFlowTypes.UPDATE_FIRMWARE:
            const isYapFirmwareInProgress = false;

            if (!isYapFirmwareInProgress) return false;

            break;
        case uamFlowTypes.SYNCHRONIZE:
            const isSyncInProgress = selectIsConnectInProgress(isHolder);

            if (!isSyncInProgress) return false;

            break;
        case uamFlowTypes.FIND_MY_DEVICE:
            const isFindMyDeviceInProgress = selectIsFindMyDeviceInProgress();

            if (!isFindMyDeviceInProgress) return false;

            break;
        case uamFlowTypes.RESET_DEVICE:
            const isResetDeviceInProgress = selectIsResetDeviceInProgress(isHolder);

            if (!isResetDeviceInProgress) return false;

            break;
        case uamFlowTypes.DATA_COLLECTION:
            const isDataCollectionInProgress = false;

            if (!isDataCollectionInProgress) return false;

            break;
        case uamFlowTypes.CONNECT:
            const isConnectInProgress = selectIsConnectInProgress(isHolder);
            const isConnectedToCm = selectIsConnectedToCm(isHolder);

            if (!isConnectInProgress || isConnectedToCm) return false;

            break;
        default:
            break;
    }

    return true;
};

const getFlow = (type, isHolder) => {
    //!! used to resolve undefined, null and false as same
    return flows.find((flow) => flow.type === type && !!flow.isHolder === !!isHolder);
};

const addFlow = (requestId, type, isHolder) => {
    clearFlow(type, isHolder);

    if (requestId || type === uamFlowTypes.CONNECT) {
        flows.push({type, isHolder, requestId});
    }
};

const clearFlow = (type, isHolder) => {
    //!! used to resolve undefined, null and false as same
    flows = flows.filter((flow) => flow.type !== type || !!flow.isHolder !== !!isHolder);
    clearFlowTimeout(type, isHolder);
    clearFlowRequestCheck(type, isHolder);
};

const getFlowTimeout = (type, isHolder) => {
    //!! used to resolve undefined, null and false as same
    return flowsTimeout.find((flow) => flow.type === type && !!flow.isHolder === !!isHolder);
};

const addFlowTimeout = (instance, type, isHolder) => {
    clearFlowTimeout(type, isHolder);
    flowsTimeout.push({type, isHolder, instance});
};

const clearFlowTimeout = (type, isHolder) => {
    const flowTimeout = getFlowTimeout(type, isHolder);
    const {instance} = flowTimeout || {};

    if (instance) {
        clearTimeout(instance);
    }

    //!! used to resolve undefined, null and false as same
    flowsTimeout = flowsTimeout.filter((flow) => flow.type !== type || !!flow.isHolder !== !!isHolder);
};

const getFlowRequestCheck = (type, isHolder) => {
    //!! used to resolve undefined, null and false as same
    return flowsRequestCheck.find((flow) => flow.type === type && !!flow.isHolder === !!isHolder);
};

const addFlowRequestCheck = (instance, type, isHolder) => {
    clearFlowRequestCheck(type, isHolder);
    flowsRequestCheck.push({type, isHolder, instance});
};

const clearFlowRequestCheck = (type, isHolder) => {
    const flowTimeout = getFlowRequestCheck(type, isHolder);
    const {instance} = flowTimeout || {};

    if (instance) {
        clearTimeout(instance);
    }

    //!! used to resolve undefined, null and false as same
    flowsRequestCheck = flowsRequestCheck.filter((flow) => flow.type !== type || !!flow.isHolder !== !!isHolder);
};

const clearAllFlows = () => {
    flows = [];
    flowsTimeout.forEach((x) => clearTimeout(x.instance));
    flowsRequestCheck.forEach((x) => clearTimeout(x.instance));
    flowsTimeout = [];
    flowsRequestCheck = [];
};

export default {
    clearAllFlows,
    createFlow,
    clearFlow,
    getFlow,
};
